import PropTypes from 'prop-types';
import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';

const FieldContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: ${(props) => props.width || '100%'};
  max-width: ${(props) => props.maxWidth || 'none'};
  min-width: 0;
  box-sizing: border-box;
  position: relative;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  font-size: 14px;
  font-weight: 500;
  color: ${(props) => (props.theme.name === 'dark' ? '#E5E7EB' : '#4B5563')};
`;

const Required = styled.span`
  color: #ef4444;
`;

const InputWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 12px 36px 12px 14px;
  background: ${(props) =>
    props.theme.name === 'dark' ? 'rgba(255, 255, 255, 0.05)' : '#ffffff'};
  border: 1px solid
    ${(props) => {
      if (props.error) return '#ef4444';
      if (props.isOpen) return props.theme?.colors?.primary || '#fa4616';
      return props.theme.name === 'dark'
        ? 'rgba(255, 255, 255, 0.1)'
        : 'rgba(0, 0, 0, 0.15)';
    }};
  border-radius: 6px;
  color: ${(props) => (props.theme.name === 'dark' ? '#ffffff' : '#1F2937')};
  font-size: 14px;
  outline: none;
  transition: border-color 0.2s, box-shadow 0.2s;
  box-sizing: border-box;

  &:focus {
    border-color: ${(props) =>
      props.error ? '#ef4444' : props.theme?.colors?.primary || '#fa4616'};
    box-shadow: 0 0 0 3px
      ${(props) =>
        props.error ? 'rgba(239, 68, 68, 0.1)' : 'rgba(250, 70, 22, 0.1)'};
  }

  &::placeholder {
    color: ${(props) => (props.theme.name === 'dark' ? '#6B7280' : '#9CA3AF')};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const DropdownIcon = styled.div`
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%)
    ${(props) => (props.isOpen ? 'rotate(180deg)' : 'rotate(0)')};
  pointer-events: none;
  transition: transform 0.2s;
  color: ${(props) => (props.theme.name === 'dark' ? '#9CA3AF' : '#6B7280')};
  display: flex;
  align-items: center;
`;

const ClearButton = styled.button`
  position: absolute;
  right: 32px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  padding: 4px;
  cursor: pointer;
  color: ${(props) => (props.theme.name === 'dark' ? '#9CA3AF' : '#6B7280')};
  display: flex;
  align-items: center;
  border-radius: 4px;

  &:hover {
    color: #ef4444;
    background: rgba(239, 68, 68, 0.1);
  }
`;

const DropdownList = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  margin-top: 4px;
  background: ${(props) =>
    props.theme.name === 'dark' ? '#1F2937' : '#ffffff'};
  border: 1px solid
    ${(props) =>
      props.theme.name === 'dark'
        ? 'rgba(255, 255, 255, 0.1)'
        : 'rgba(0, 0, 0, 0.15)'};
  border-radius: 6px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  max-height: 200px;
  overflow-y: auto;
  z-index: 1000;
`;

const DropdownItem = styled.div`
  padding: 10px 14px;
  cursor: pointer;
  font-size: 14px;
  color: ${(props) => (props.theme.name === 'dark' ? '#ffffff' : '#1F2937')};
  background: ${(props) =>
    props.isHighlighted
      ? props.theme.name === 'dark'
        ? 'rgba(250, 70, 22, 0.2)'
        : 'rgba(250, 70, 22, 0.1)'
      : 'transparent'};

  &:hover {
    background: ${(props) =>
      props.theme.name === 'dark'
        ? 'rgba(255, 255, 255, 0.1)'
        : 'rgba(0, 0, 0, 0.05)'};
  }
`;

const NoResults = styled.div`
  padding: 12px 14px;
  font-size: 14px;
  color: ${(props) => (props.theme.name === 'dark' ? '#9CA3AF' : '#6B7280')};
  text-align: center;
`;

const CreateOption = styled.div`
  padding: 10px 14px;
  cursor: pointer;
  font-size: 14px;
  color: ${(props) => props.theme?.colors?.primary || '#fa4616'};
  background: ${(props) =>
    props.isHighlighted
      ? props.theme.name === 'dark'
        ? 'rgba(250, 70, 22, 0.2)'
        : 'rgba(250, 70, 22, 0.1)'
      : 'transparent'};
  border-top: 1px solid
    ${(props) =>
      props.theme.name === 'dark'
        ? 'rgba(255, 255, 255, 0.1)'
        : 'rgba(0, 0, 0, 0.1)'};
  display: flex;
  align-items: center;
  gap: 8px;

  &:hover {
    background: ${(props) =>
      props.theme.name === 'dark'
        ? 'rgba(250, 70, 22, 0.2)'
        : 'rgba(250, 70, 22, 0.1)'};
  }
`;

const LoadingText = styled.div`
  padding: 12px 14px;
  font-size: 14px;
  color: ${(props) => (props.theme.name === 'dark' ? '#9CA3AF' : '#6B7280')};
  text-align: center;
`;

const HelperText = styled.div`
  margin-top: 6px;
  font-size: 12px;
  color: ${(props) => (props.error ? '#ef4444' : '#64748B')};
`;

/**
 * SearchableSelect - A searchable dropdown component
 *
 * @param {string} label - Label text for the select
 * @param {boolean} required - Whether the field is required
 * @param {string|number} value - The selected value
 * @param {Function} onChange - Callback when value changes (receives {label, value})
 * @param {string} placeholder - Placeholder text
 * @param {Array} options - Array of {label, value} objects
 * @param {boolean} loading - Whether options are loading
 * @param {Function} onSearch - Callback when search text changes (for async search)
 * @param {number} minSearchLength - Minimum characters before searching
 * @param {string} noResultsText - Text to show when no results
 * @param {string} helperText - Helper/error text
 * @param {boolean} error - Whether the field has an error
 * @param {string} width - Width of the field
 * @param {string} maxWidth - Max width of the field
 * @param {boolean} disabled - Whether the field is disabled
 * @param {boolean} creatable - Whether new options can be created
 * @param {Function} onCreateOption - Callback when creating a new option (receives label string, should return Promise with {label, value})
 * @param {string} createLabel - Label for the create option (default: 'Create')
 */
const SearchableSelect = ({
  label,
  required = false,
  value,
  onChange,
  placeholder = 'Start typing to search...',
  options = [],
  loading = false,
  onSearch,
  minSearchLength = 0,
  noResultsText = 'No results found',
  helperText,
  error = false,
  width = '100%',
  maxWidth,
  disabled = false,
  creatable = false,
  onCreateOption,
  createLabel = 'Create',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [isCreating, setIsCreating] = useState(false);
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  // Find the selected option to display its label
  const selectedOption = options.find((opt) => opt.value === value);

  // Filter options based on search text (for local filtering)
  const filteredOptions =
    searchText.length >= minSearchLength
      ? options.filter((opt) =>
          opt.label.toLowerCase().includes(searchText.toLowerCase())
        )
      : options;

  // Determine if we should show the create option
  const trimmedSearch = searchText.trim();
  const exactMatch = options.some(
    (opt) => opt.label.toLowerCase() === trimmedSearch.toLowerCase()
  );
  const showCreateOption =
    creatable &&
    onCreateOption &&
    trimmedSearch.length > 0 &&
    !exactMatch &&
    !isCreating;

  // Handle creating a new option
  const handleCreateOption = async () => {
    if (!onCreateOption || !trimmedSearch) return;

    setIsCreating(true);
    try {
      const newOption = await onCreateOption(trimmedSearch);
      if (newOption) {
        setSearchText(newOption.label);
        onChange(newOption);
      }
      setIsOpen(false);
    } catch (err) {
      // Error handling is left to the onCreateOption callback
    } finally {
      setIsCreating(false);
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        setIsOpen(false);
        // Reset search text to selected value's label when closing
        if (selectedOption) {
          setSearchText(selectedOption.label);
        } else {
          setSearchText('');
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [selectedOption]);

  // Update search text when value changes externally
  useEffect(() => {
    if (selectedOption) {
      setSearchText(selectedOption.label);
    } else if (!value) {
      setSearchText('');
    }
  }, [value, selectedOption]);

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setSearchText(newValue);
    setIsOpen(true);
    setHighlightedIndex(-1);

    if (onSearch && newValue.length >= minSearchLength) {
      onSearch(newValue);
    }
  };

  const handleInputFocus = () => {
    setIsOpen(true);
    // Select all text on focus for easy replacement
    if (inputRef.current) {
      inputRef.current.select();
    }
  };

  const handleSelectOption = (option) => {
    setSearchText(option.label);
    setIsOpen(false);
    onChange(option);
  };

  const handleClear = (e) => {
    e.stopPropagation();
    setSearchText('');
    onChange(null);
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  // Total selectable items (options + create option if shown)
  const totalItems = filteredOptions.length + (showCreateOption ? 1 : 0);
  const createOptionIndex = showCreateOption ? filteredOptions.length : -1;

  const handleKeyDown = (e) => {
    if (!isOpen) {
      if (e.key === 'ArrowDown' || e.key === 'Enter') {
        setIsOpen(true);
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev < totalItems - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : prev));
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex === createOptionIndex && showCreateOption) {
          handleCreateOption();
        } else if (highlightedIndex >= 0 && filteredOptions[highlightedIndex]) {
          handleSelectOption(filteredOptions[highlightedIndex]);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        if (selectedOption) {
          setSearchText(selectedOption.label);
        }
        break;
      default:
        break;
    }
  };

  return (
    <FieldContainer width={width} maxWidth={maxWidth} ref={containerRef}>
      {label && (
        <Label>
          {label} {required && <Required>*</Required>}
        </Label>
      )}
      <InputWrapper>
        <SearchInput
          ref={inputRef}
          type="text"
          value={searchText}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          error={error}
          isOpen={isOpen}
        />
        {value && !disabled && (
          <ClearButton type="button" onClick={handleClear} tabIndex={-1}>
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </ClearButton>
        )}
        <DropdownIcon isOpen={isOpen}>
          <svg width="12" height="12" viewBox="0 0 12 12">
            <path fill="currentColor" d="M6 9L1 4h10z" />
          </svg>
        </DropdownIcon>

        {isOpen && (
          <DropdownList>
            {loading || isCreating ? (
              <LoadingText>
                {isCreating ? 'Creating...' : 'Loading...'}
              </LoadingText>
            ) : filteredOptions.length === 0 && !showCreateOption ? (
              <NoResults>
                {searchText.length < minSearchLength
                  ? `Type at least ${minSearchLength} characters to search`
                  : noResultsText}
              </NoResults>
            ) : (
              <>
                {filteredOptions.map((option, index) => (
                  <DropdownItem
                    key={option.value}
                    isHighlighted={index === highlightedIndex}
                    onClick={() => handleSelectOption(option)}
                  >
                    {option.label}
                  </DropdownItem>
                ))}
                {showCreateOption && (
                  <CreateOption
                    isHighlighted={highlightedIndex === createOptionIndex}
                    onClick={handleCreateOption}
                  >
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <line x1="12" y1="5" x2="12" y2="19" />
                      <line x1="5" y1="12" x2="19" y2="12" />
                    </svg>
                    {createLabel} &quot;{trimmedSearch}&quot;
                  </CreateOption>
                )}
              </>
            )}
          </DropdownList>
        )}
      </InputWrapper>
      {helperText && <HelperText error={error}>{helperText}</HelperText>}
    </FieldContainer>
  );
};

SearchableSelect.propTypes = {
  label: PropTypes.string,
  required: PropTypes.bool,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
        .isRequired,
    })
  ),
  loading: PropTypes.bool,
  onSearch: PropTypes.func,
  minSearchLength: PropTypes.number,
  noResultsText: PropTypes.string,
  helperText: PropTypes.string,
  error: PropTypes.bool,
  width: PropTypes.string,
  maxWidth: PropTypes.string,
  disabled: PropTypes.bool,
  creatable: PropTypes.bool,
  onCreateOption: PropTypes.func,
  createLabel: PropTypes.string,
};

export default SearchableSelect;
